// Public Home Service - handles data fetching for the public homepage
// This service provides mock data for development and can be extended to fetch real data from API

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  rating: number;
  text: string;
  location: string;
}

export interface Caregiver {
  id: string;
  name: string;
  specialty: string;
  rating: number;
  reviewCount: number;
  location: string;
  avatar?: string;
  bio: string;
  verified: boolean;
  priceRange: string;
}

export interface StatsData {
  totalCaregivers: number;
  totalAppointments: number;
  averageRating: number;
  verifiedCaregivers: number;
}

class PublicHomeService {
  // Mock data for development
  private mockTestimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Patient',
      rating: 5,
      text: 'Careva made it so easy to find a physical therapist who could help me recover from my knee surgery. The caregiver was professional, knowledgeable, and really cared about my progress.',
      location: 'San Francisco, CA',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'Patient',
      rating: 5,
      text: 'I needed occupational therapy after my stroke, and Careva connected me with an amazing therapist. The booking process was simple, and the care I received was exceptional.',
      location: 'Austin, TX',
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      role: 'Caregiver',
      rating: 5,
      text: 'As a physical therapist, Careva has given me the flexibility to work with patients on my own schedule. The platform is user-friendly and the patients are wonderful to work with.',
      location: 'Miami, FL',
    },
    {
      id: '4',
      name: 'David Thompson',
      role: 'Patient',
      rating: 5,
      text: 'Finding quality senior care for my mother was challenging until I discovered Careva. The caregivers are all background-checked and truly compassionate professionals.',
      location: 'Seattle, WA',
    },
    {
      id: '5',
      name: 'Lisa Park',
      role: 'Caregiver',
      rating: 5,
      text: 'Careva has transformed how I provide home health services. The platform makes scheduling easy and helps me build lasting relationships with my patients.',
      location: 'Denver, CO',
    },
  ];

  private mockCaregivers: Caregiver[] = [
    {
      id: '1',
      name: 'Dr. Sarah Williams',
      specialty: 'Physical Therapist',
      rating: 4.9,
      reviewCount: 127,
      location: 'San Francisco, CA',
      bio: 'Licensed PT with 8+ years experience in orthopedic rehabilitation and sports medicine.',
      verified: true,
      priceRange: '$120-150/hour',
    },
    {
      id: '2',
      name: 'Michael Rodriguez',
      specialty: 'Occupational Therapist',
      rating: 4.8,
      reviewCount: 89,
      location: 'Austin, TX',
      bio: 'Specialized in stroke recovery and adaptive equipment training for daily living.',
      verified: true,
      priceRange: '$100-130/hour',
    },
    {
      id: '3',
      name: 'Emily Chen',
      specialty: 'Senior Care Specialist',
      rating: 4.9,
      reviewCount: 156,
      location: 'Miami, FL',
      bio: 'Compassionate caregiver with expertise in dementia care and mobility assistance.',
      verified: true,
      priceRange: '$25-35/hour',
    },
    {
      id: '4',
      name: 'Dr. James Thompson',
      specialty: 'Home Health Nurse',
      rating: 4.7,
      reviewCount: 203,
      location: 'Seattle, WA',
      bio: 'RN with 12+ years in home health, specializing in post-surgical care and chronic conditions.',
      verified: true,
      priceRange: '$80-100/hour',
    },
    {
      id: '5',
      name: 'Lisa Park',
      specialty: 'Mental Health Counselor',
      rating: 4.8,
      reviewCount: 94,
      location: 'Denver, CO',
      bio: 'Licensed therapist providing individual and family counseling services.',
      verified: true,
      priceRange: '$90-120/hour',
    },
    {
      id: '6',
      name: 'Robert Johnson',
      specialty: 'Physical Therapist',
      rating: 4.9,
      reviewCount: 178,
      location: 'Chicago, IL',
      bio: 'Specialized in geriatric physical therapy and fall prevention programs.',
      verified: true,
      priceRange: '$110-140/hour',
    },
  ];

  private mockStats: StatsData = {
    totalCaregivers: 12500,
    totalAppointments: 45000,
    averageRating: 4.8,
    verifiedCaregivers: 98,
  };

  // Simulate API delay
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getTestimonials(): Promise<Testimonial[]> {
    await this.delay(500); // Simulate API call
    return this.mockTestimonials;
  }

  async getFeaturedCaregivers(): Promise<Caregiver[]> {
    await this.delay(600); // Simulate API call
    return this.mockCaregivers;
  }

  async getStats(): Promise<StatsData> {
    await this.delay(400); // Simulate API call
    return this.mockStats;
  }

  // Future: Replace with real API calls
  async getTestimonialsFromAPI(): Promise<Testimonial[]> {
    try {
      // TODO: Replace with actual API endpoint
      // const response = await fetch('/api/testimonials');
      // return await response.json();
      return this.mockTestimonials;
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      return this.mockTestimonials; // Fallback to mock data
    }
  }

  async getFeaturedCaregiversFromAPI(): Promise<Caregiver[]> {
    try {
      // TODO: Replace with actual API endpoint
      // const response = await fetch('/api/caregivers/featured');
      // return await response.json();
      return this.mockCaregivers;
    } catch (error) {
      console.error('Error fetching featured caregivers:', error);
      return this.mockCaregivers; // Fallback to mock data
    }
  }

  async getStatsFromAPI(): Promise<StatsData> {
    try {
      // TODO: Replace with actual API endpoint
      // const response = await fetch('/api/stats');
      // return await response.json();
      return this.mockStats;
    } catch (error) {
      console.error('Error fetching stats:', error);
      return this.mockStats; // Fallback to mock data
    }
  }
}

export const publicHomeService = new PublicHomeService();
